<template>
  <div class="form-wrapper">
    <button class="back-link" @click="$emit('cancel')">← Back to Dashboard</button>
    
    <div class="blog-article">
      <h2 class="main-title">Draft a New Blog</h2>

      <div class="input-group">
        <label for="title">Blog Title</label>
        <input 
          id="title" 
          type="text" 
          v-model="blog.title" 
          placeholder="e.g., Unveiling Douala's Top Braiding Trends" 
          class="text-input"
        >
      </div>

      <div class="input-group">
        <label>Blog Content</label>
        <div class="editor-container">
          <div ref="editor"></div>
        </div>
      </div>

      <div class="engagement-section">
        <button 
          class="cta-button" 
          @click="submitChanges" 
          :disabled="!isValid">
          Publish Blog
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css'; 
import axiosInstance from '@/services/AxiosClient';

export default {
  name: "NewBlog",
  props: {
    stylistData: {
      type: Object,
      required: true
    }
  },
  emits: ['submit-blog', 'cancel'],
  data() {
    return {
      blog: {
        title: '',      
        content: '',    
        author: this.stylistData.username,
        stylist_id: this.stylistData.id
      },
      quillInstance: null, 
    }
  },
  computed: {
    isValid() {
        return this.blog.title.trim().length > 0 && this.blog.content.length > 0;
    }
  },
  async mounted() {
    await this.$nextTick(); 
    this.initQuill();
  },
  beforeUnmount() {
    if (this.quillInstance) {
      // Turn off listeners and disable the editor so it stops tracking the mouse/cursor
      this.quillInstance.off('text-change');
      this.quillInstance.disable();
      this.quillInstance = null;
    }
  },
  methods: {

    initQuill() {
      if (!this.$refs.editor) return;
      const toolbarOptions = [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'align' : []}],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ];

      this.quillInstance = new Quill(this.$refs.editor, {
        modules: { toolbar: toolbarOptions },
        theme: 'snow',
        placeholder: 'Write your styling tips here...'
      });

      // 1. Toolbar Click Handler
      this.quillInstance.getModule('toolbar').addHandler('image', () => {
        this.selectLocalImage();
      });

      // 2. Intercept Drag-and-Drop
      this.quillInstance.root.addEventListener('drop', async (e) => {
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
          
          if (imageFiles.length > 0) {
            e.preventDefault(); 
            for (const file of imageFiles) {
              await this.uploadAndInsertImage(file);
            }
          }
        }
      });

      // 3. Intercept Copy-Paste
      this.quillInstance.root.addEventListener('paste', async (e) => {
        const clipboardData = e.clipboardData || window.clipboardData;
        const items = clipboardData?.items;
        
        if (items) {
          const imageFiles = [];
          for (const item of items) {
            if (item.type.startsWith('image/')) {
              imageFiles.push(item.getAsFile());
            }
          }

          if (imageFiles.length > 0) {
            e.preventDefault(); 
            for (const file of imageFiles) {
              await this.uploadAndInsertImage(file);
            }
          }
        }
      });

      // Synchronize text changes to local stringified variable (Consolidated into single listener)
      this.quillInstance.on('text-change', () => {
        this.blog.content = JSON.stringify(this.quillInstance.getContents());
        if (typeof this.markEditted === 'function') this.markEditted();
      });
    },
    selectLocalImage() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;
        
        // Reuses the core upload utility to avoid duplicated code blocks
        await this.uploadAndInsertImage(file);
      };
    },
    async uploadAndInsertImage(file) {
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await axiosInstance.post('/blog/upload-image', formData);
        const range = this.quillInstance.getSelection(true);
        
        this.quillInstance.insertEmbed(range.index, 'image', response.data.url);
        this.quillInstance.setSelection(range.index + 1);
        
        if (typeof this.markEditted === 'function') {
          this.markEditted();
        }
      } catch (err) {
        console.error("Failed to upload inline image asset", err);
      }
    },
    submitChanges() {
        if(this.isValid) {
            this.$emit('submit-blog', { state: 'new', blog: this.blog });
        }
    }
  }
}
</script>

<style scoped>
.back-link {
    background: none; border: none;
    color: #A0522D;
    font-size: 1rem; font-weight: 600;
    cursor: pointer; margin-bottom: 2rem; padding: 0;
}
.back-link:hover { color: #5D0E1F; }

.blog-article {
    background-color: white; padding: 3rem;
    border-radius: 1rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.main-title {
    font-family: 'Playfair Display', serif; font-size: 2.5rem;
    color: #5D0E1F; margin-bottom: 2rem;
}

.input-group { margin-bottom: 2rem; }
.input-group label {
    display: block; font-weight: 600;
    color: #1c1917; margin-bottom: 0.5rem;
}

.text-input {
    width: 100%; padding: 1rem;
    border: 2px solid #eaeaea; border-radius: 8px;
    font-size: 1rem; font-family: 'Inter', sans-serif;
    outline: none; transition: border-color 0.3s ease;
}
.text-input:focus { border-color: #FBBF24; }

.editor-container { border: 2px solid #eaeaea; border-radius: 8px; background: #fff; }
.editor-container:focus-within { border-color: #FBBF24; }
.editor-container :deep(.ql-toolbar) { border: none; border-bottom: 1px solid #eaeaea; border-radius: 8px 8px 0 0; }
.editor-container :deep(.ql-container) { border: none; font-size: 1.1rem; min-height: 300px; font-family: 'Inter', sans-serif; }

.cta-button {
    background-color: #5D0E1F; color: white;
    padding: 1rem 2.5rem; border: none; border-radius: 30px; 
    font-weight: 700; font-size: 1.1rem; cursor: pointer;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2); 
    transition: all 0.2s ease;
}
.cta-button:disabled { background-color: #ccc; cursor: not-allowed; box-shadow: none; }
</style>